import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translations, languageOptions, poojaTypes } from '../../constants/poojaStepsData';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { updatePoojaStep, updatePoojaType, updatePoojaMantra } from '../../actions.any';
// Helper function to render formatted text with bold support
const renderFormattedText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            // Remove ** markers and make bold
            const boldText = part.slice(2, -2);
            return React.createElement("strong", { key: index, style: { fontWeight: '700', color: '#000000' } }, boldText);
        }
        return React.createElement("span", { key: index }, part);
    });
};
export default function PoojaStepsPanel() {
    const dispatch = useDispatch();
    // Local state for UI-only properties
    const [language, setLanguage] = useState('hi');
    const [viewingStepIndex, setViewingStepIndex] = useState(0);
    // Refs for scrolling
    const scrollContainerRef = useRef(null);
    const stepRefsArray = useRef([]);
    // Redux state for synchronized properties
    const { currentStepIndex, completedSteps, selectedPoojaType, activeMantraIndex } = useSelector((state) => ({
        currentStepIndex: state['features/chat']?.poojaSteps?.currentStepIndex ?? 0,
        completedSteps: state['features/chat']?.poojaSteps?.completedSteps ?? [],
        selectedPoojaType: state['features/chat']?.poojaSteps?.selectedPoojaType ?? 'rudrabhishek',
        activeMantraIndex: state['features/chat']?.poojaSteps?.activeMantraIndex ?? 0
    }));
    // Check if current user is a moderator
    const isModerator = useSelector((state) => isLocalParticipantModerator(state));
    // Check if data channel is open for real-time sync
    const isDataChannelOpen = useSelector((state) => state['features/base/conference']?.dataChannelOpen ?? false);
    // Get current pooja data based on selection
    const currentPoojaType = poojaTypes.find(p => p.id === selectedPoojaType) || poojaTypes[0];
    const currentPoojaSteps = currentPoojaType.steps;
    // Use viewing step for display, but keep currentStepIndex for moderator control
    const displayStepIndex = viewingStepIndex;
    const currentStep = currentPoojaSteps && currentPoojaSteps[displayStepIndex] ? currentPoojaSteps[displayStepIndex] : null;
    const T = translations[language];
    // Send current step when data channel opens for moderator
    useEffect(() => {
        if (isDataChannelOpen && isModerator) {
            // Send current step to all users when data channel opens
            dispatch(updatePoojaStep(currentStepIndex, completedSteps));
        }
    }, [isDataChannelOpen, isModerator, currentStepIndex, completedSteps, dispatch]);
    // Navigation functions for moderators (actual step control)
    const goToNextStep = () => {
        if (isModerator) {
            // Check if there are multiple mantras in current step
            const currentStep = currentPoojaSteps[currentStepIndex];
            const hasMultipleMantras = currentStep && currentStep.mantras && currentStep.mantras.length > 1;
            if (hasMultipleMantras && activeMantraIndex < (currentStep.mantras?.length || 0) - 1) {
                // Navigate to next mantra within current step
                dispatch(updatePoojaMantra(activeMantraIndex + 1));
            }
            else if (currentStepIndex < currentPoojaSteps.length - 1) {
                // Move to next step
                const newStepIndex = currentStepIndex + 1;
                // Mark all steps from 0 to currentStepIndex as completed
                // This handles cases where user jumps forward and then moves to next step
                const newCompletedSteps = currentPoojaSteps
                    .slice(0, currentStepIndex + 1) // Get all steps from 0 to currentStepIndex
                    .map(step => step.id); // Extract their IDs
                // Dispatch Redux action to update and broadcast
                dispatch(updatePoojaStep(newStepIndex, newCompletedSteps));
                setViewingStepIndex(newStepIndex); // Update view to match
                dispatch(updatePoojaMantra(0)); // Reset to first mantra of new step
            }
        }
    };
    const goToPreviousStep = () => {
        if (isModerator) {
            // Check if there are multiple mantras in current step and we're not on first mantra
            const currentStep = currentPoojaSteps[currentStepIndex];
            const hasMultipleMantras = currentStep && currentStep.mantras && currentStep.mantras.length > 1;
            if (hasMultipleMantras && activeMantraIndex > 0) {
                // Navigate to previous mantra within current step
                dispatch(updatePoojaMantra(activeMantraIndex - 1));
            }
            else if (currentStepIndex > 0) {
                // Move to previous step
                const newStepIndex = currentStepIndex - 1;
                // Remove completion status from current and future steps
                // Only keep steps BEFORE the target step as completed
                const newCompletedSteps = completedSteps.filter(stepId => {
                    // Find which step this ID corresponds to
                    const stepIndex = currentPoojaSteps.findIndex(s => s.id === stepId);
                    // Keep only if it's before the target step
                    return stepIndex < newStepIndex;
                });
                // Dispatch Redux action to update and broadcast
                dispatch(updatePoojaStep(newStepIndex, newCompletedSteps));
                setViewingStepIndex(newStepIndex); // Update view to match
                // Set to last mantra of previous step if it has multiple mantras
                const prevStep = currentPoojaSteps[newStepIndex];
                if (prevStep && prevStep.mantras && prevStep.mantras.length > 1) {
                    dispatch(updatePoojaMantra(prevStep.mantras.length - 1));
                }
                else {
                    dispatch(updatePoojaMantra(0));
                }
            }
        }
    };
    // Viewing function for all users (just changes what they see)
    const viewStep = (stepIndex) => {
        if (isModerator) {
            // Moderators control the actual step for everyone
            dispatch(updatePoojaStep(stepIndex, completedSteps));
        }
        // All users can view any step
        setViewingStepIndex(stepIndex);
    };
    const toggleLanguage = () => {
        setLanguage(language === 'hi' ? 'en' : 'hi');
    };
    // Sync viewing step with moderator changes
    React.useEffect(() => {
        // Ensure viewingStepIndex doesn't exceed array bounds
        const maxStepIndex = currentPoojaSteps ? currentPoojaSteps.length - 1 : 0;
        const safeStepIndex = Math.min(currentStepIndex, maxStepIndex);
        setViewingStepIndex(safeStepIndex);
        // Don't automatically reset mantra index - let the navigation functions handle it
        // This prevents resetting when participants join and sync
    }, [currentStepIndex, currentPoojaSteps]);
    // Validate mantra index when step changes
    React.useEffect(() => {
        const currentStep = currentPoojaSteps && currentPoojaSteps[currentStepIndex];
        if (currentStep && currentStep.mantras) {
            const maxMantraIndex = currentStep.mantras.length - 1;
            if (activeMantraIndex > maxMantraIndex) {
                // If mantra index is out of bounds, reset to last mantra
                dispatch(updatePoojaMantra(maxMantraIndex));
            }
        }
    }, [currentStepIndex, currentPoojaSteps, activeMantraIndex, dispatch]);
    // Auto-scroll to center the active step in the horizontal panel
    React.useEffect(() => {
        const activeStepElement = stepRefsArray.current[viewingStepIndex];
        const scrollContainer = scrollContainerRef.current;
        if (activeStepElement && scrollContainer) {
            // Calculate the scroll position to center the active step
            const containerWidth = scrollContainer.clientWidth;
            const stepOffsetLeft = activeStepElement.offsetLeft;
            const stepWidth = activeStepElement.offsetWidth;
            // Calculate scroll position to center the step
            const scrollPosition = stepOffsetLeft - (containerWidth / 2) + (stepWidth / 2);
            // Smooth scroll to the calculated position
            scrollContainer.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
        }
    }, [viewingStepIndex, currentPoojaSteps]);
    const getStepStatus = (stepIndex) => {
        // Safety check for array bounds and undefined steps
        if (!currentPoojaSteps || stepIndex >= currentPoojaSteps.length || !currentPoojaSteps[stepIndex]) {
            return 'pending';
        }
        if (completedSteps.includes(currentPoojaSteps[stepIndex].id)) {
            return 'completed';
        }
        if (stepIndex === displayStepIndex) {
            return 'active';
        }
        return 'pending';
    };
    return (React.createElement("div", { style: {
            padding: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF8E7 100%)',
            fontFamily: 'Inter, sans-serif',
            color: '#5C3D2E',
            fontSize: '14px'
        } },
        React.createElement("div", { style: { textAlign: 'center', marginBottom: '4px', position: 'relative' } },
            React.createElement("div", { style: { marginBottom: '4px' } },
                isModerator ? (React.createElement("select", { value: selectedPoojaType, onChange: (e) => {
                        dispatch(updatePoojaType(e.target.value));
                        setViewingStepIndex(0); // Reset to first step
                    }, style: {
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '2px solid #FF7B00',
                        backgroundColor: 'white',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#FF7B00',
                        marginRight: '8px',
                        minWidth: '150px'
                    } }, poojaTypes.map(pooja => (React.createElement("option", { key: pooja.id, value: pooja.id }, pooja.name[language] || pooja.name.hi))))) : (React.createElement("div", { style: {
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '2px solid #FF7B00',
                        backgroundColor: '#FFF8E7',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#FF7B00',
                        marginRight: '8px',
                        minWidth: '150px',
                        display: 'inline-block',
                        textAlign: 'center'
                    } }, currentPoojaType.name[language] || currentPoojaType.name.hi)),
                React.createElement("select", { value: language, onChange: (e) => setLanguage(e.target.value), style: {
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '2px solid #4CAF50',
                        backgroundColor: 'white',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#4CAF50',
                        minWidth: '100px'
                    } }, languageOptions.map(lang => (React.createElement("option", { key: lang.code, value: lang.code }, lang.name))))),
            React.createElement("div", { style: {
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                } },
                React.createElement("div", { style: {
                        background: isModerator ? '#4CAF50' : '#FFA726',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '0.6rem',
                        fontWeight: '600'
                    } },
                    isModerator ? '👑' : '👤',
                    " "),
                React.createElement("div", { style: {
                        background: isDataChannelOpen ? '#4CAF50' : '#FF9800',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '0.6rem',
                        fontWeight: '600'
                    }, title: isDataChannelOpen ?
                        (language === 'hi' ? 'रीयल-टाइम सिंक चालू' : 'Real-time sync active') :
                        (language === 'hi' ? 'सिंक प्रतीक्षा में' : 'Sync pending') }, isDataChannelOpen ? '🔄' : '⏳'))),
        React.createElement("div", { ref: scrollContainerRef, style: {
                background: 'rgba(255, 255, 255, 0.8)',
                padding: '1px',
                borderRadius: '8px',
                marginBottom: '6px',
                overflowX: 'auto'
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    gap: '6px',
                    minWidth: 'max-content',
                    paddingBottom: '2px'
                } }, currentPoojaSteps && currentPoojaSteps.map((step, index) => {
                const status = getStepStatus(index);
                const isActive = status === 'active';
                const isCompleted = status === 'completed';
                return (React.createElement("div", { key: step.id, ref: el => stepRefsArray.current[index] = el, style: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: '50px',
                        cursor: isModerator ? 'pointer' : 'default'
                    }, onClick: isModerator ? () => viewStep(index) : undefined },
                    React.createElement("div", { style: {
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.6rem',
                            fontWeight: '600',
                            marginBottom: '4px',
                            background: isCompleted ? '#FF7B00' : isActive ? '#FFD700' : 'white',
                            border: `2px solid ${isCompleted ? '#FF7B00' : isActive ? '#FFD700' : '#ccc'}`,
                            color: isCompleted ? 'white' : isActive ? '#FF7B00' : '#666',
                            animation: isActive ? 'pulse 2s infinite' : 'none'
                        } }, isCompleted ? '✓' : step.id),
                    React.createElement("div", { style: {
                            fontSize: '0.55rem',
                            textAlign: 'center',
                            lineHeight: '1.1',
                            color: isActive ? '#FF7B00' : 'rgba(92, 61, 46, 0.7)',
                            fontWeight: isActive ? '600' : '500',
                            maxWidth: '50px',
                            wordWrap: 'break-word'
                        } }, (step.title[language] || step.title.hi).length > 12
                        ? (step.title[language] || step.title.hi).substring(0, 12) + '...'
                        : (step.title[language] || step.title.hi))));
            }))),
        React.createElement("div", { style: {
                flex: '1',
                background: 'white',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(92, 61, 46, 0.2)',
                overflowY: 'auto',
                marginBottom: '12px'
            } },
            React.createElement("div", { style: { textAlign: 'center', marginBottom: '12px' } }, (() => {
                const viewedStepStatus = getStepStatus(displayStepIndex);
                const isCurrentlyActiveStep = currentStep && !completedSteps.includes(currentStep.id) &&
                    currentPoojaSteps && currentStepIndex === currentPoojaSteps.findIndex(step => !completedSteps.includes(step.id));
                let statusText = T.pending;
                let bgColor = 'rgba(158, 158, 158, 0.3)';
                let textColor = '#9E9E9E';
                if (currentStep && completedSteps.includes(currentStep.id)) {
                    statusText = T.completed;
                    bgColor = 'rgba(76, 175, 80, 0.3)';
                    textColor = '#4CAF50';
                }
                else if (displayStepIndex === currentStepIndex && isCurrentlyActiveStep) {
                    statusText = T.ongoing;
                    bgColor = 'rgba(255, 215, 0, 0.3)';
                    textColor = '#FF7B00';
                }
                return (React.createElement("span", { style: {
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        fontSize: '0.65rem',
                        fontWeight: '600',
                        background: bgColor,
                        color: textColor
                    } }, statusText));
            })()),
            React.createElement("h2", { style: {
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    color: '#5C3D2E',
                    margin: '0 0 8px 0',
                    textAlign: 'center',
                    lineHeight: '1.2'
                } }, currentStep ? (currentStep.title[language] || currentStep.title.hi) : 'Loading...'),
            React.createElement("p", { style: {
                    fontSize: '0.75rem',
                    lineHeight: '1.4',
                    color: 'rgba(92, 61, 46, 0.9)',
                    marginBottom: '12px',
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap'
                } }, currentStep ? renderFormattedText(currentStep.description[language] || currentStep.description.hi) : 'Loading...'),
            currentStep && currentStep.mantras && currentStep.mantras.length > 0 && (React.createElement("div", { style: {
                    borderTop: '1px dashed rgba(92, 61, 46, 0.2)',
                    paddingTop: '12px',
                    marginTop: '12px'
                } },
                currentStep.mantras.length > 1 && (React.createElement("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: 'rgba(255, 123, 0, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 123, 0, 0.2)'
                    } },
                    React.createElement("span", { style: {
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            color: '#FF7B00'
                        } },
                        language === 'hi' ? 'मंत्र' : 'Mantra',
                        " ",
                        activeMantraIndex + 1,
                        " ",
                        language === 'hi' ? 'का' : 'of',
                        " ",
                        currentStep.mantras.length),
                    React.createElement("div", { style: {
                            display: 'flex',
                            gap: '4px'
                        } }, currentStep.mantras.map((_, index) => (React.createElement("div", { key: index, style: {
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: activeMantraIndex === index ? '#FF7B00' : 'rgba(255, 123, 0, 0.3)',
                            transition: 'all 0.2s ease'
                        } })))))),
                currentStep.mantras[activeMantraIndex] && (React.createElement("div", null,
                    React.createElement("div", { style: { marginBottom: '12px' } },
                        React.createElement("h3", { style: {
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#FF7B00',
                                margin: '0 0 6px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            } },
                            React.createElement("span", null, "\uD83D\uDCD6"),
                            React.createElement("span", null,
                                language === 'hi' ? 'मंत्र' : 'Mantra',
                                currentStep.mantras.length > 1 ? ` ${activeMantraIndex + 1}` : '')),
                        React.createElement("div", { style: {
                                backgroundColor: '#FFD70033',
                                padding: '12px',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                lineHeight: '1.6',
                                color: '#5C3D2E',
                                whiteSpace: 'pre-wrap',
                                fontFamily: language === 'hi' ? "'Lohit Devanagari', sans-serif" : 'Inter, sans-serif',
                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                minHeight: '60px'
                            } }, currentStep.mantras[activeMantraIndex].mantra[language] || currentStep.mantras[activeMantraIndex].mantra.hi)),
                    React.createElement("div", null,
                        React.createElement("h3", { style: {
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#FF7B00',
                                margin: '0 0 6px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            } },
                            React.createElement("span", null, "\uD83D\uDCA1"),
                            React.createElement("span", null,
                                language === 'hi' ? 'अर्थ' : 'Meaning',
                                currentStep.mantras.length > 1 ? ` ${activeMantraIndex + 1}` : '')),
                        React.createElement("div", { style: {
                                fontSize: '0.75rem',
                                lineHeight: '1.5',
                                color: 'rgba(92, 61, 46, 0.9)',
                                whiteSpace: 'pre-wrap',
                                fontFamily: language === 'hi' ? "'Lohit Devanagari', sans-serif" : 'Inter, sans-serif',
                                padding: '12px',
                                backgroundColor: 'rgba(92, 61, 46, 0.05)',
                                borderRadius: '8px',
                                border: '1px solid rgba(92, 61, 46, 0.1)',
                                minHeight: '60px'
                            } }, currentStep.mantras[activeMantraIndex].meaning[language] || currentStep.mantras[activeMantraIndex].meaning.hi))))))),
        isModerator && (React.createElement("div", { style: {
                padding: '8px 12px',
                background: 'rgba(92, 61, 46, 0.8)',
                borderRadius: '6px',
                color: 'white'
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                } },
                React.createElement("button", { onClick: toggleLanguage, style: {
                        background: 'white',
                        color: '#5C3D2E',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                    } }, language === 'hi' ? 'English' : 'हिन्दी'),
                React.createElement("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    } },
                    React.createElement("button", { onClick: goToPreviousStep, disabled: currentStepIndex === 0 || !isModerator, style: {
                            background: 'white',
                            color: '#5C3D2E',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            cursor: (currentStepIndex === 0 || !isModerator) ? 'not-allowed' : 'pointer',
                            opacity: (currentStepIndex === 0 || !isModerator) ? 0.5 : 1
                        }, title: !isModerator ? (language === 'hi' ? 'केवल मॉडरेटर ही चरण बदल सकते हैं' : 'Only moderators can change steps') : '' }, (() => {
                        const currentStep = currentPoojaSteps[currentStepIndex];
                        const hasMultipleMantras = currentStep && currentStep.mantras && currentStep.mantras.length > 1;
                        const isFirstMantra = hasMultipleMantras && activeMantraIndex === 0;
                        if (hasMultipleMantras && !isFirstMantra) {
                            return language === 'hi' ? 'पिछला मंत्र' : 'Prev Mantra';
                        }
                        else if (hasMultipleMantras && isFirstMantra) {
                            return language === 'hi' ? 'पिछला चरण' : 'Prev Step';
                        }
                        else {
                            return T.prevButton;
                        }
                    })()),
                    React.createElement("span", { style: {
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            padding: '0 4px'
                        } },
                        currentStepIndex + 1,
                        "/",
                        currentPoojaSteps.length),
                    React.createElement("button", { onClick: goToNextStep, disabled: currentStepIndex === currentPoojaSteps.length - 1 || !isModerator, style: {
                            background: '#FF7B00',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            cursor: (currentStepIndex === currentPoojaSteps.length - 1 || !isModerator) ? 'not-allowed' : 'pointer',
                            opacity: (currentStepIndex === currentPoojaSteps.length - 1 || !isModerator) ? 0.5 : 1
                        }, title: !isModerator ? (language === 'hi' ? 'केवल मॉडरेटर ही चरण बदल सकते हैं' : 'Only moderators can change steps') : '' }, (() => {
                        const currentStep = currentPoojaSteps[currentStepIndex];
                        const hasMultipleMantras = currentStep && currentStep.mantras && currentStep.mantras.length > 1;
                        const isLastMantra = hasMultipleMantras && activeMantraIndex === (currentStep.mantras?.length || 0) - 1;
                        if (hasMultipleMantras && !isLastMantra) {
                            return language === 'hi' ? 'अगला मंत्र' : 'Next Mantra';
                        }
                        else if (hasMultipleMantras && isLastMantra) {
                            return language === 'hi' ? 'अगला चरण' : 'Next Step';
                        }
                        else {
                            return T.nextButton;
                        }
                    })()))))),
        React.createElement("style", null, `
                @keyframes pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7); }
                    50% { box-shadow: 0 0 0 6px rgba(255, 215, 0, 0); }
                }
            `)));
}
